import { useEffect, useState } from "react";
import { useRootStore } from "../hooks/useRootStore";
import type { Employee } from '../data/Employee';
import Avatar from '@mui/material/Avatar';
import { Box, Button, Card, CardHeader, Checkbox, Divider, IconButton, Link } from "@mui/material";
import { EmployeeAccessLevel, getIssueEmployeeAccessLevelNameRu } from "../data/EmployeeAccessLevel";
import { User } from "../data/User";
import { Agent } from "../data/Agent";
import type { Person } from "../data/Person";
import { AgentType } from "../data/AgentType";
import { CustomerEmployeeAccessLevel, getIssueCustomerEmployeeAccessLevelNameRu } from "../data/CustomerEmployeeAccessLevel";
import Schedule from "./Schedule";
import './Employee.css';
import moment from "moment";
import { getGenderNameRu } from "../data/Gender";
import { CustomerEmployee } from "../data/CustomerEmployee";
import ChangeEmployeeDialog from "./ChangeEmployeeDialog";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ChangeCustomerEmployeeDialog from "./ChangeCustomerEmployeeDialog";
import { UserType } from "../data/UserType";
import { VacationRequest } from "../data/VacationRequest";
import useDeviceMedia from '../hooks/useDeviceMedia';

export default function Employee() {
    const { api, routerStore, userProfile } = useRootStore();
    const { isMobilePortrait } = useDeviceMedia();
    const id = routerStore.routerState.params.id as unknown as number;

    const [loading, setLoading] = useState(true);
    const [agent, setAgent] = useState<Agent>({} as Agent);
    const [dataChanging, setDataChanging] = useState(false);

    const [vacationRequests, setVacationRequests] = useState([] as VacationRequest[]);
    const [vacationRequestsLoading, setVacationRequestsLoading] = useState(false);

    const [vacationBalanceLoaded, setVacationBalanceLoaded] = useState(false);
    const [vacationBalance, setVacationBalance] = useState(0);

    useEffect(() => {
        if (id == userProfile?.ID && userProfile?.UserType == UserType.Employee) {
            api.getEmployee(id).then(e => {
                setAgent(e);
                setLoading(false);
                if (e.AgentType == AgentType.Employee) {
                    api.getVacationBalance(id).then(b => {
                        setVacationBalance(b);
                        setVacationBalanceLoaded(true);
                    })
                }
            });
        } else {
            api.getSharedEmployeeProfile(id).then(e => {
                setAgent(e);
                setLoading(false);

                if (e.AgentType == AgentType.Employee) {
                    api.getVacationBalance(id).then(b => {
                        setVacationBalance(b);
                        setVacationBalanceLoaded(true);
                    })
                }

                if ((e as Employee).Head != null && userProfile?.ID == (e as Employee).Head?.ID) {
                    setVacationRequestsLoading(true);
                    api.getEmployeeVacationRequests(id).then(r => {
                        if (r.length == 0) {
                            document.getElementById('vacation-requests')!.remove();
                        } else {
                            setVacationRequests(r);
                            setVacationRequestsLoading(false);
                        }
                    });
                }
            });
        }

    }, [id, api, userProfile?.ID, userProfile?.UserType]);

    const [asideTop, setAsideTop] = useState(0);
    const [appStateHeight, setAppStateHeight] = useState(0);

    useEffect(() => {
        setAsideTop((document.getElementsByTagName('header')[0]).offsetHeight + 2);
        setAppStateHeight((document.getElementsByClassName('app-state')[0] as HTMLElement).offsetHeight);
    }, []);

    const person = agent as Person;
    const employee = agent.AgentType == AgentType.Employee ? agent as Employee : null;
    const customerEmployee = agent.AgentType == AgentType.CustomerEmployee ? agent as CustomerEmployee : null;
    const user = ((agent as User).Login ?? '') == '' ? null : agent as User;
    const me = (id == userProfile?.ID);

    const handleApproveVacationClick = async (vacationDocumentID: number) => {
        api.approveVacation(vacationDocumentID, id).then(() => {
            if (vacationRequests.length == 1) {
                document.getElementById('vacation-requests')!.remove();
            } else {
                const newRequests = vacationRequests.filter(x => x.VacationDocumentID != vacationDocumentID);
                setVacationRequests(newRequests);
            }
        })
    }

    return (
        loading ? 'Загрузка' :
            <Box className='employee'>
                {
                    dataChanging
                        ? employee
                            ? <ChangeEmployeeDialog employee={employee!} onSave={(changed: Employee) => { setDataChanging(false); setAgent(changed); }} onCancel={() => setDataChanging(false)} />
                            : <ChangeCustomerEmployeeDialog employee={(user ?? customerEmployee)!} onSave={(changed: CustomerEmployee | User) => { setDataChanging(false); setAgent(changed); }} onCancel={() => setDataChanging(false)} />
                        : ''
                }
                <Box style={{ top: asideTop, height: 'calc(100vh - ' + (asideTop + appStateHeight + 20) + 'px)' }} className="employee-aside" id="employee-aside">
                    <Box className='employee-intro'>
                        <Avatar src={person.Avatar}></Avatar>
                        <Box className='employee-name'>{agent.Name}</Box>
                        {!me ? '' :
                            <IconButton
                                className="edit-btn"
                                sx={{ p: 0, position: 'absolute', top: 10, right: 20 }}
                                onClick={() => setDataChanging(true)}
                                title='Изменить свои данные'
                            >
                                <MoreHorizIcon />
                            </IconButton>
                        }
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                        <Card>
                            {!customerEmployee ? '' :
                                <>
                                    <Box>Клиент: <Link href={`clients/${customerEmployee.Customer.ID}`}>{customerEmployee.Customer.Name}</Link></Box>
                                    <Box>Должность: {customerEmployee.Position}</Box>
                                </>
                            }
                            <Box>День рождения: {person.BirthDate ? moment(person.BirthDate).format('DD.MM.YYYY') : ''}</Box>
                            <Box>Пол: {getGenderNameRu(person.Gender)}</Box>
                            <Box>Телефон: <Link href={`tel:${person.Phone}`}>{person.Phone}</Link></Box>
                            <Box>Email: <Link href={`mailto:${person.Email}`}>{person.Email}</Link></Box>
                            <Box>Telegram: <Link href={`https://t.me/${person.Telegram?.replace('@', '')}`}>{person.Telegram}</Link></Box>
                            <Box>Skype: <Link href={`skype:${person.Skype}?chat`}>{person.Skype}</Link></Box>
                        </Card>
                    </Box>
                    {!employee ? '' :
                        <Card>
                            <Box>Принят: {employee.HireDate ? moment(employee.HireDate).format('DD.MM.YYYY') : ''}</Box>
                            <Box>{employee.FireDate ? ("Уволен: " + moment(employee.FireDate).format('DD.MM.YYYY')) : ''}</Box>
                            {me
                                ?
                                employee.CurrentHourRate > 0
                                    ?
                                    <>
                                        <Box>Ставка: {employee.CurrentHourRate} ₽/час</Box>
                                        <Box>Наценка за предпочтение: {employee.IssuePreferredRate}%</Box>
                                    </>
                                    : employee.CurrentSalary > 0
                                        ? <Box>Зарплата: {employee.CurrentSalary}</Box>
                                        : ''
                                : <Box>Наценка за предпочтение: {employee.IssuePreferredRate}%</Box>
                            }
                            <Box>Автоназначение задач: <Checkbox sx={{p:0}} disabled checked={employee.AutoIssueAssignment} /></Box>
                            <Box>Шеф: <Link href={`employees/${employee.Head?.ID}`}>{employee.Head?.Name}</Link></Box>
                            <Box>Остаток отпуска: {vacationBalanceLoaded ? `${vacationBalance} дней` : 'подсчет...'}</Box>
                        </Card>
                    }
                    {!user ? '' :
                        <Card sx={{ display: 'flex', flexDirection: "column", alignItems: 'flex-start' }}>
                            <CardHeader title="Пользователь трекера" className="card-header" />
                            <Box>Логин: {(agent as User).Login}</Box>
                            <Box>
                                Уровень доступа: {
                                    agent.AgentType == AgentType.Employee
                                        ? getIssueEmployeeAccessLevelNameRu((agent as User).AccessLevelID as EmployeeAccessLevel)
                                        : getIssueCustomerEmployeeAccessLevelNameRu((agent as User).AccessLevelID as CustomerEmployeeAccessLevel)
                                }
                            </Box>
                        </Card>
                    }
                    {employee && me ? 
                        <Card sx={{ display: 'flex', flexDirection: "column", alignItems: 'flex-start' }}>
                            <CardHeader title="Платежные реквизиты" className="card-header" />
                            <Box>ИНН: {employee.Inn}</Box>
                            <Box>№ счета: {employee.PaymentBankAccount}</Box>
                            <Box>Банк: {employee.PaymentBankName}</Box>
                            <Box>БИК: {employee.PaymentBIC}</Box>
                            <Box>Кор.счет: {employee.PaymentCorrAccNo}</Box>
                            <Box>Адрес банка: {employee.PaymentBankLocation}</Box>
                            <Box>Получатель платежа: {employee.PaymentName}</Box>
                            <Box>ИНН для перевода: {employee.PaymentVATno}</Box>
                        </Card>
                        : ''    
                    }
                </Box>
                {isMobilePortrait ? '' :
                    <>
                        <Divider orientation="vertical" flexItem />
                        <Box flex={1} sx={{ p: 2 }}>
                            {employee ?
                                <>
                                    {employee.Head?.ID == userProfile?.ID ?
                                        <Box id="vacation-requests" sx={{ textAlign: 'left', p: 2, mb: 2, border: '1px solid #bbbbbb' }}>
                                            <CardHeader sx={{ p: 0, mb: 2 }} title={vacationRequestsLoading ? 'Поиск запросов на отпуск...' : 'Запросы на отпуск'} />
                                            {vacationRequests.length > 0 ?
                                                <>
                                                    <Box sx={{ mb: 1 }}>
                                                        Остаток отпуска: {vacationBalanceLoaded ? `${vacationBalance} дней` : 'подсчет...'}
                                                    </Box>
                                                    <Box>
                                                        {vacationRequests.map(vr =>
                                                            <Box id={`vacation-request-${vr.VacationDocumentID}`}>
                                                                {vr.VacationDates.length} дней: {vr.VacationDates.map(vd => moment(vd).format('DD.MM.YYYY')).join(', ')}
                                                                <Button onClick={() => handleApproveVacationClick(vr.VacationDocumentID)}>Подтвердить</Button>
                                                            </Box>)}
                                                    </Box>
                                                </>
                                                : ''
                                            }
                                        </Box>
                                        : ''
                                    }
                                    <Schedule employeeAgentID={id} />
                                </> : ''
                            }
                        </Box>
                    </>
                }
            </Box>
    );
}